import {
    ERC721TokenType,
    FlatTokenWithAmountAndToAddress,
    ImmutableXClient,
    LinkParams,
    valueOrThrowTE,
} from '@imtbl/imx-sdk';
import { BigNumber } from 'ethers';
import React, { useContext, useEffect, useState } from 'react';

import { ErrorMessageProps } from '../components';
import { GeneralError } from '../components/ErrorMessage/GeneralError';
import { LinkUiCoreContext } from '../context/App.context';
import { getTokenWithDetails } from '../lib';
import { TransferInfoWithTokenInfo } from '../types/SharedTransfer.types';

export const useGetBatchNftTransferTokenDetails = ({
    client,
    params,
    userAddress,
    uniqueCollectionsInBatch,
    userOwnedAssets,
}: {
    client?: ImmutableXClient;
    params?: LinkParams.BatchNftTransfer;
    userAddress?: string;
    uniqueCollectionsInBatch?: number;
    userOwnedAssets: any[];
}) => {
    const [transferDetails, setTransferDetails] = useState<TransferInfoWithTokenInfo[]>();
    const { setErrorLog } = useContext(LinkUiCoreContext);

    useEffect(() => {
        if (!params || !client || !userAddress || params.length === 0 || uniqueCollectionsInBatch === undefined) return;

        async function getAllOrdersTokenInfoByListAssets() {
            if (
                !params ||
                params.length === 0 ||
                !client ||
                !userAddress ||
                uniqueCollectionsInBatch !== 1 ||
                userOwnedAssets.length === 0
            )
                return;
            try {
                const assetsOwnedInParams = userOwnedAssets.filter((asset) =>
                    params.find((token: any) => token.tokenId === asset.token_id),
                );

                const mappedResponseWithParams = assetsOwnedInParams.map((asset, index) => ({
                    tokenDetails: {
                        toAddress: '',
                        tokenAddress: asset.token_address,
                        tokenId: asset.token_id,
                        type: ERC721TokenType.ERC721,
                        quantity: BigNumber.from(1),
                        amount: '1',
                        token: {
                            data: {
                                tokenAddress: asset.token_address,
                                tokenId: asset.token_id,
                            },
                            type: ERC721TokenType.ERC721,
                        },
                        asset,
                    },
                    originalParams: {
                        ...params[index],
                    },
                }));

                if (mappedResponseWithParams.length === 0 || mappedResponseWithParams.length !== params.length) {
                    setErrorLog('User does not own all assets in batch', {
                        heading: 'Validation Error',
                        body: (
                            <GeneralError
                                messages={[
                                    'User does not own all of the assets in the batch for transferring',
                                    `Current connected user: ${userAddress}`,
                                ]}
                            />
                        ),
                    } as ErrorMessageProps);
                }

                setTransferDetails(mappedResponseWithParams as any as TransferInfoWithTokenInfo[]);
            } catch (err) {
                setErrorLog(err);
            }
        }

        // @NOTE: For each order, get all the token details:
        async function getAllOrdersTokenInfoByIndividualAsset() {
            if (!client || !params) {
                return;
            }
            try {
                const transferTokenDetailsResponse = await Promise.all(
                    [
                        ...params.map((token) => {
                            return [
                                valueOrThrowTE(getTokenWithDetails(token as FlatTokenWithAmountAndToAddress)(client)),
                            ];
                        }),
                    ].flat(),
                );
                const mergeResponseWithParams = transferTokenDetailsResponse.map((token, index) => ({
                    tokenDetails: {
                        ...params[index],
                        ...token,
                    },
                    originalParams: params[index],
                }));
                setTransferDetails(mergeResponseWithParams);
            } catch (err) {
                setErrorLog(err);
            }
        }

        if (uniqueCollectionsInBatch === 1 && userOwnedAssets.length > 0) {
            getAllOrdersTokenInfoByListAssets();
        } else if (uniqueCollectionsInBatch > 1) {
            getAllOrdersTokenInfoByIndividualAsset();
        }
    }, [params, client, userAddress, uniqueCollectionsInBatch, userOwnedAssets, setErrorLog]);

    return transferDetails;
};
